import { useContext, useState } from "react"
import Box from "@mui/material/Box"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import LocalLibraryRoundedIcon from "@mui/icons-material/LocalLibraryRounded"
import BookRoundedIcon from "@mui/icons-material/BookRounded"
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded"
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded"
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded"
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded"
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded"
import { drawerWidth } from "../layouts/AppLayout"
import { Auth } from "aws-amplify"
import { Link, useLocation, useNavigate } from "react-router-dom"
import routes from "../../constants/routes"
import UserContext from "../../contexts/user"
import {
  AppBar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
} from "@mui/material"
import { Stack } from "@mui/system"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import ScheduleIcon from "@mui/icons-material/Schedule"

const styles = {
  icon: {
    fontSize: "1.8rem",
  },
  iconSmall: {
    fontSize: "1.8rem",
  },
  drawer: {
    boxSizing: "border-box",
    backgroundColor: "primary.main",
    color: "#fff",
    px: 2,
    py: 1,
    pt: 4,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
}

const allowedRoutes = {
  ADMIN: [
    routes.ACADEMIC.HOME,
    routes.ACCOUNTING.HOME,
    routes.ASSETS.HOME,
    routes.ADMIN.HOME,
    routes.PROFILE,
  ],
  STUDENT: [routes.CLASSES.HOME, routes.ACADEMIC.HOME, routes.PROFILE],
  STAFF: [
    routes.ACADEMIC.HOME,
    routes.ACCOUNTING.HOME,
    routes.ASSETS.HOME,
    routes.ADMIN.HOME,
    routes.PROFILE,
  ],
  TEACHER: [routes.CLASSES.HOME, routes.ACADEMIC.HOME, routes.ACCOUNTING.HOME, routes.PROFILE],
}

const mobileNav = {
  ADMIN: [
    routes.ACADEMIC.HOME,
    routes.ACCOUNTING.HOME,
    routes.ACTIVITY,
    routes.PROFILE,
    routes.ASSETS.HOME,
    routes.ADMIN.HOME,
    routes.CALENDAR,
  ],
  STUDENT: [
    routes.CLASSES.HOME,
    routes.ACADEMIC.HOME,
    routes.DAILY_SCHEDULE,
    routes.PROFILE,
    routes.CALENDAR,
  ],
  STAFF: [
    routes.ACADEMIC.HOME,
    routes.ACCOUNTING.HOME,
    routes.ACTIVITY,
    routes.PROFILE,
    routes.ASSETS.HOME,
    routes.ADMIN.HOME,
    routes.CALENDAR,
  ],
  TEACHER: [
    routes.CLASSES.HOME,
    routes.ACADEMIC.HOME,
    routes.DAILY_SCHEDULE,
    routes.PROFILE,
    routes.ACCOUNTING.HOME,
    routes.CALENDAR,
  ],
}

const navItems = {
  [routes.CLASSES.HOME]: {
    name: "Classes",
    Icon: <LocalLibraryRoundedIcon sx={styles.icon} />,
    link: routes.CLASSES.HOME,
  },
  [routes.ACADEMIC.HOME]: {
    name: "Academic",
    Icon: <BookRoundedIcon sx={styles.icon} />,
    link: routes.ACADEMIC.HOME,
  },
  [routes.ACCOUNTING.HOME]: {
    name: "Accounting",
    Icon: <AccountBalanceRoundedIcon sx={styles.icon} />,
    link: routes.ACCOUNTING.HOME,
  },
  [routes.ASSETS.HOME]: {
    name: "Assets",
    Icon: <WarehouseRoundedIcon sx={styles.icon} />,
    link: routes.ASSETS.HOME,
  },
  [routes.ADMIN.HOME]: {
    name: "Admin",
    Icon: <AdminPanelSettingsRoundedIcon sx={styles.icon} />,
    link: routes.ADMIN.HOME,
  },
  [routes.PROFILE]: {
    name: "Profile",
    Icon: <AccountCircleRoundedIcon sx={styles.icon} />,
    link: routes.PROFILE,
  },
  [routes.ACTIVITY]: {
    name: "Activity",
    Icon: <ScheduleIcon sx={styles.icon} />,
    link: routes.ACTIVITY,
  },
  [routes.DAILY_SCHEDULE]: {
    name: "Daily Schedule",
    Icon: <ScheduleIcon sx={styles.icon} />,
    link: routes.DAILY_SCHEDULE,
  },
  [routes.CALENDAR]: {
    name: "Calendar",
    Icon: <CalendarMonthIcon sx={styles.icon} />,
    link: routes.CALENDAR,
  },
}

const NavList = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      mt: "50px",
    }}
  >
    {children}
  </Box>
)

const NavItem = ({ name, Icon, link, active, action }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 0.2,
    }}
  >
    <Link to={link || "/"}>
      <Tooltip title={name} placement="right-end">
        <IconButton
          onClick={action}
          sx={{
            color: "white",
            background: active ? "rgba(255,255,255,0.2)" : "",
            borderRadius: 3,
            "&:hover": {
              background: "rgba(255,255,255,0.1)",
            },
          }}
        >
          {Icon}
        </IconButton>
      </Tooltip>
    </Link>
  </Box>
)

const DrawerContent = ({ userRole, logout }) => {
  const location = useLocation()
  return (
    <>
      <NavList>
        {allowedRoutes[userRole]
          ?.map((route) => navItems[route])
          .map((props, index) => (
            <NavItem
              key={index}
              {...props}
              active={location.pathname.startsWith(props.link)}
            />
          ))}
      </NavList>
      <NavList>
        <NavItem
          name="Log Out"
          Icon={<LogoutRoundedIcon sx={styles.iconSmall} />}
          action={logout}
        />
      </NavList>
    </>
  )
}

const Sidebar = () => {
  const { userRole, setUser, setUserRole } = useContext(UserContext)
  const navigate = useNavigate()

  const handleSignout = () => {
    Auth.signOut().then(() => {
      setUser(null)
      setUserRole("")
      navigate("/")
    })
  }

  const location = useLocation()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Box
        component="nav"
        sx={{
          height: "calc(100% - 100px)",
          width: { sm: 60 },
          flexShrink: { sm: 0 },
          zIndex: 900,
          display: {
            xs: "none",
            sm: "block",
          },
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="permanent"
          sx={{
            mt: "150px !important",
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              ...styles.drawer,
              zIndex: 900,
            },
          }}
          open
        >
          <DrawerContent logout={handleSignout} userRole={userRole} />
        </Drawer>
      </Box>
      <AppBar
        position="fixed"
        color="primary"
        sx={{ display: { sm: "none" }, top: "auto", bottom: 0 }}
      >
        <Toolbar>
          <Stack
            direction="horizontal"
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            {userRole
              ? mobileNav[userRole]
                  .slice(0, 4)
                  .map((route) => navItems[route])
                  .map((props, index) => (
                    <NavItem
                      key={index}
                      {...props}
                      active={location.pathname === props.link}
                    />
                  ))
              : ""}
            <Tooltip title={"More"} placement="right-end">
              <IconButton
                onClick={handleClick}
                sx={{
                  color: "white",
                  borderRadius: 3,
                  "&:hover": {
                    background: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <MoreVertIcon sx={styles.icon} />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
              {userRole
                ? mobileNav[userRole]
                    .slice(4)
                    .map((route) => navItems[route])
                    .map((props, index) => (
                      <Link key={index} to={props.link}>
                        <MenuItem onClick={handleClose}>
                          <ListItemIcon>{props.Icon}</ListItemIcon>
                          <ListItemText sx={{ color: "gray" }}>
                            {props.name}
                          </ListItemText>
                        </MenuItem>
                      </Link>
                    ))
                : ""}
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>
    </>
  )
}

export default Sidebar
